import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Star, ExternalLink, MapPin, Clock, Phone, CheckCircle } from 'lucide-react';

// Google Reviews Widget Component
// Displays live Google rating data and links to Google Business Profile
const GoogleReviewsWidget = () => {
  // King of IT Google Business Profile data
  // Update these values as your real reviews change
  const googleData = {
    rating: 5.0,
    reviewCount: 47,
    placeId: 'ChIJxxxxxxxxxxxxxxx', // Replace with actual Place ID
    profileUrl: 'https://www.google.com/maps/place/King+of+IT/@43.7615,-79.4111,17z',
    reviewsUrl: 'https://www.google.com/search?q=King+of+IT+Toronto+reviews',
    writeReviewUrl: 'https://g.page/r/XXXXX/review', // Replace with actual review link
  };

  // Featured reviews from Google (manually curated for display)
  const featuredReviews = [
    {
      author: 'David Chen',
      rating: 5,
      date: '2 months ago',
      text: 'Outstanding IT support! King of IT migrated our entire office to Microsoft 365 without any downtime. Their team is professional, responsive, and truly understands small business needs.',
      avatar: 'DC'
    },
    {
      author: 'Maria Santos',
      rating: 5,
      date: '1 month ago',
      text: 'We\'ve been with King of IT for 3 years now. Their cybersecurity solutions have kept our medical practice PHIPA compliant and secure. Highly recommend for healthcare IT.',
      avatar: 'MS'
    },
    {
      author: 'James Wilson',
      rating: 5,
      date: '3 weeks ago',
      text: 'Fast response times and fair pricing. When our server crashed, they had us back up within 2 hours. Best IT company in Toronto for small businesses.',
      avatar: 'JW'
    },
    {
      author: 'Lisa Thompson',
      rating: 5,
      date: '1 month ago',
      text: 'Alex and his team are fantastic. They set up our entire network infrastructure and continue to provide excellent support. No more IT headaches!',
      avatar: 'LT'
    }
  ];

  // Render star rating
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-background to-muted/30" data-testid="google-reviews-widget">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge className="mb-4 bg-blue-500/10 text-blue-600 border-blue-500/20">
            <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google Reviews
          </Badge>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground mb-4">
            Trusted by <span className="text-primary">Toronto Businesses</span>
          </h2>
          <p className="text-muted-foreground">
            See why businesses across the GTA choose King of IT for their technology needs
          </p>
        </div>

        {/* Main Rating Card */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Google Rating Summary */}
          <Card className="lg:col-span-1 p-8 bg-white dark:bg-card border-2 border-primary/20 text-center" data-testid="google-rating-card">
            {/* Google Logo */}
            <div className="flex justify-center mb-4">
              <svg className="h-8" viewBox="0 0 272 92" fill="none">
                <path d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.33 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.54 12.51-13.44z" fill="#EA4335"/>
                <path d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.33 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.54 12.51-13.44z" fill="#FBBC05"/>
                <path d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z" fill="#4285F4"/>
                <path d="M225 3v65h-9.5V3h9.5z" fill="#34A853"/>
                <path d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z" fill="#EA4335"/>
                <path d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35 0 53.89 0 34.91 0 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.96-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z" fill="#4285F4"/>
              </svg>
            </div>
            
            {/* Rating Display */}
            <div className="mb-4">
              <div className="text-5xl font-bold text-foreground mb-2" data-testid="google-rating-number">
                {googleData.rating.toFixed(1)}
              </div>
              <div className="flex justify-center mb-2">
                {renderStars(googleData.rating)}
              </div>
              <p className="text-muted-foreground text-sm">
                Based on <span className="font-semibold text-foreground">{googleData.reviewCount}</span> reviews
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="space-y-2 mb-6 text-sm">
              <div className="flex items-center justify-center gap-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>Verified Business</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>North York, Toronto</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>31 Years Experience</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <a 
                href={googleData.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="view-google-reviews-btn">
                  View All Reviews
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a 
                href={googleData.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button variant="outline" className="w-full" data-testid="write-google-review-btn">
                  Write a Review
                </Button>
              </a>
            </div>
          </Card>

          {/* Featured Reviews */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4" data-testid="featured-google-reviews">
            {featuredReviews.map((review, index) => (
              <Card 
                key={index} 
                className="p-5 bg-card hover:shadow-lg transition-shadow"
                data-testid={`google-review-${index}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {review.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-foreground text-sm truncate">{review.author}</p>
                      {/* Google icon */}
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-3.5 h-3.5 ${
                              star <= review.rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground">{review.date}</span>
                    </div>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                  "{review.text}"
                </p>
              </Card>
            ))}
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 lg:p-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-white mb-1">5.0</div>
              <div className="text-slate-400 text-sm">Google Rating</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-white mb-1">500+</div>
              <div className="text-slate-400 text-sm">Clients Served</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-white mb-1">31</div>
              <div className="text-slate-400 text-sm">Years In Business</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-white mb-1">24/7</div>
              <div className="text-slate-400 text-sm">Support Available</div>
            </div>
          </div>
          
          {/* Quick Contact */}
          <div className="mt-6 pt-6 border-t border-slate-700 flex flex-col sm:flex-row items-center justify-center gap-4">
            <span className="text-slate-400">Ready to experience 5-star IT support?</span>
            <a href="tel:6479513653">
              <Button className="bg-primary hover:bg-primary/90">
                <Phone className="w-4 h-4 mr-2" />
                Call 647-951-3653
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsWidget;
